import { useEffect, useState } from "react";

export interface ScriptProps {
    root: any;
    src: HTMLScriptElement["src"] | null;
    checkForExisting?: Boolean;
    [key: string]: any;
}

type ErrorState = ErrorEvent | null;

export function useScript({ root, src, checkForExisting = false, ...attributes }: ScriptProps): [boolean, ErrorState] {
    const [loading, setLoading] = useState(Boolean(src));
    const [error, setError] = useState<ErrorState>(null);

    useEffect(() => {
        if (!src || !root) {
            return;
        }

        if (checkForExisting) {
            const existing = root.querySelectorAll(`script[src="${src}"]`);
            if (existing.length > 0) {
                setLoading(false);
                return;
            }
        }

        const scriptEl = root.createElement("script");
        scriptEl.setAttribute("src", src);

        Object.keys(attributes).forEach((key) => {
            if (scriptEl[key] === undefined) {
                scriptEl.setAttribute(key, attributes[key]);
            } else {
                scriptEl[key] = attributes[key];
            }
        });

        const handleLoad = () => {
            setLoading(false);
        };
        const handleError = (error: ErrorEvent) => {
            setLoading(false);
            setError(error);
        };

        scriptEl.addEventListener("load", handleLoad);
        scriptEl.addEventListener("error", handleError);

        root.body.appendChild(scriptEl);

        return () => {
            scriptEl.removeEventListener("load", handleLoad);
            scriptEl.removeEventListener("error", handleError);
        };
        // we need to ignore the attributes as they're a new object per call, so we'd never skip an effect call
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, root]);

    return [loading, error];
}

const SCRIPT_TAG = "script";

export const useScriptWithCallback = (url: string, onLoad?: () => void) => {
    useEffect(() => {
        const scripts = document.getElementsByTagName(SCRIPT_TAG);
        for (const scriptEl of Array.from(scripts)) {
            // prevent loading same script twice
            if (scriptEl.src === url) {
                if (onLoad) {
                    onLoad();
                }
                return;
            }
        }
        const script = document.createElement(SCRIPT_TAG);

        script.src = url;
        script.async = true;

        if (onLoad) {
            script.addEventListener("load", onLoad);
        }

        document.head.appendChild(script);

        return () => {
            if (onLoad) {
                script.removeEventListener("load", onLoad);
            }
            document.head.removeChild(script);
        };
    }, [url, onLoad]);
};
